import React, { useEffect, useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";

const DonationsOverviewPage = () => {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState("");

  useEffect(() => {
    fetchDonations();
  }, []);

  const fetchDonations = async () => {
    setLoading(true);
    try {      
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/donations`);
      const data = await res.json();
      setDonations(data);
    } catch (err) {
      console.error("Error fetching donations:", err);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Donor Name",
        accessorKey: "user_name",
      },
      {
        header: "Club Name",
        accessorKey: "club_name",
      },
      {
        header: "Amount (EUR)",
        accessorKey: "amount",
      },
      {
        header: "Tokens",
        accessorKey: "tokens",
      },
      {
        header: "Created At",
        accessorKey: "created_at",
        cell: info => new Date(info.getValue()).toLocaleDateString(),
      },
    ],
    []
  );

  const table = useReactTable({
    data: donations,
    columns,
    state: { globalFilter },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
  });

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Donations Overview</h1>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          {/* Global Search */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search..."
              className="border p-2 rounded w-full sm:w-1/2"
              value={globalFilter ?? ""}
              onChange={e => setGlobalFilter(e.target.value)}
            />
          </div>

          {/* Table */}
          <div className="overflow-x-auto border rounded">
            <table className="min-w-full table-auto text-sm text-left text-gray-700">
              <thead className="bg-gray-100">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        className="p-3 font-semibold cursor-pointer select-none text-left"
                      >
                        <div className="flex items-center gap-1">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: "▲",
                            desc: "▼",
                          }[header.column.getIsSorted()] ?? ""}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="border-t hover:bg-gray-100 even:bg-gray-50">
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="p-3 text-left">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Prev
              </button>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
              <span>
                Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
              </span>
            </div>

            <select
              value={table.getState().pagination.pageSize}
              onChange={e => table.setPageSize(Number(e.target.value))}
              className="border p-1 rounded"
            >
              {[5, 10, 20, 50].map(size => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </div>
        </>
      )}
    </div>
  );
};

export default DonationsOverviewPage;
